import { useLanguage } from '../contexts/LanguageContext';
import SEO from '../components/SEO';
import {
  Globe, Zap, Bot, MessageCircle, BarChart2, Shield,
  ArrowRight, CheckCircle2, Layers, Lock, RefreshCw, Activity
} from 'lucide-react';

const content = {
  ar: {
    badge: 'المنصة',
    h1: 'مزايا مصممة للأعمال الذكية',
    sub: 'كل أداة في كويدوري مبنية لتعمل بذكاء، بثقة، وعلى نطاق واسع.',
    sections: [
      {
        tag: 'الذكاء',
        h: 'ذكاء متعدد اللغات من الأساس',
        desc: 'لا يتم بناء كويدوري على أنماط اللغة الإنجليزية ثم ترجمتها. النظام يفكر بالعربية والإنجليزية بشكل مستقل، مع الحفاظ على الفروق الدقيقة والسياق الثقافي الصحيح.',
        points: ['ذكاء عربي أصيل بسياق ثقافي', 'لغة مالية ومتخصصة في كلتا اللغتين', 'إنتاج محتوى متعدد اللغات بجودة عالية'],
        icon: Globe,
      },
      {
        tag: 'الأتمتة',
        h: 'خط أنابيب نشر آلي',
        desc: 'من إشارة تحليلية إلى المنشور المنشور — كل شيء آلي. الإنشاء والجدولة والتوزيع عبر قنوات متعددة دون تدخل يدوي.',
        points: ['إنشاء محتوى مدعوم بالذكاء الاصطناعي', 'الجدولة عبر قنوات متعددة', 'مراقبة آنية للأداء'],
        icon: Zap,
      },
      {
        tag: 'الوكلاء',
        h: 'وكلاء ذكاء اصطناعي محادثيون',
        desc: 'وكلاء قابلون للتخصيص، مدرّبون على بيانات خاصة بمجالك، يتحدثون بصوت علامتك التجارية عبر قنوات متعددة.',
        points: ['تدريب على بيانات خاصة بالمجال', 'دعم متعدد القنوات', 'شخصية علامة تجارية مخصصة'],
        icon: Bot,
      },
      {
        tag: 'البنية التحتية',
        h: 'هندسة قابلة للتوسع',
        desc: 'مبنية للتوسع من منتج واحد إلى منصة متعددة الصناعات. كل مكوّن معياري ومصمم للنمو.',
        points: ['بنية معيارية قابلة للتوسع', 'جاهز للعلامة التجارية الخاصة', 'مناسب للمؤسسات'],
        icon: Layers,
      },
    ],
    trustItems: ['بنية عربية أولاً', 'معالجة اللغة الطبيعية', 'آمن ومشفر', 'وقت تشغيل ٩٩.٩٪'],
  },
  en: {
    badge: 'Platform',
    h1: 'Capabilities Built for Intelligent Business',
    sub: 'Every tool in Quidory is built to operate intelligently, reliably, and at scale.',
    sections: [
      {
        tag: 'Intelligence',
        h: 'Multilingual AI from the Ground Up',
        desc: "Quidory is not built on English language patterns and then translated. The system thinks in Arabic and English independently, preserving nuance, terminology, and cultural context.",
        points: ['Native Arabic intelligence with cultural context', 'Financial and domain-specific language in both', 'High-quality multilingual content production'],
        icon: Globe,
      },
      {
        tag: 'Automation',
        h: 'Automated Publishing Pipeline',
        desc: 'From intelligence signal to published post — everything automated. Creation, scheduling, and distribution across multiple channels without manual intervention.',
        points: ['AI-powered content generation', 'Multi-channel scheduling and distribution', 'Real-time performance monitoring'],
        icon: Zap,
      },
      {
        tag: 'Agents',
        h: 'Conversational AI Agents',
        desc: 'Customizable agents, trained on your domain-specific data, speaking in your brand voice across multiple channels.',
        points: ['Trained on domain-specific data', 'Multi-channel support', 'Custom brand personality'],
        icon: Bot,
      },
      {
        tag: 'Infrastructure',
        h: 'Scalable Architecture',
        desc: 'Built to scale from a single product to a multi-industry platform. Every component is modular and designed for growth.',
        points: ['Modular, scalable architecture', 'White-label ready', 'Enterprise-grade'],
        icon: Layers,
      },
    ],
    trustItems: ['Arabic-First Architecture', 'Natural Language Processing', 'Secure & Encrypted', '99.9% Uptime'],
  },
};

const FeaturesPage = () => {
  const { language, isRTL } = useLanguage();
  const c = content[language];

  return (
    <div className="pt-16">
      <SEO
        titleEn="Platform Features — Quidory AI Intelligence"
        titleAr="مزايا المنصة — كويدوري للذكاء الاصطناعي"
        descEn="Multilingual AI core, automated publishing pipeline, conversational AI agents, and channel infrastructure. Built for intelligent business at scale."
        descAr="محور ذكاء اصطناعي متعدد اللغات، خط أنابيب نشر آلي، وكلاء محادثيون. مبني للأعمال الذكية على نطاق واسع."
        path="/features"
      />
      {/* Hero */}
      <section className="py-24 lg:py-32 bg-background relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(99,102,241,0.06)_0%,transparent_60%)]" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-xs font-semibold uppercase tracking-widest text-gold mb-4 block">{c.badge}</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-foreground mb-6">
            {c.h1}
          </h1>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto">{c.sub}</p>
        </div>
      </section>

      {/* Sections */}
      <section className="py-8 bg-background">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          {c.sections.map((sec, i) => {
            const Icon = sec.icon;
            const isEven = i % 2 === 0;
            return (
              <div
                key={i}
                data-testid={`feature-section-${i}`}
                className={`grid lg:grid-cols-2 gap-12 items-center py-16 border-b border-border last:border-0 ${!isEven && !isRTL ? 'lg:grid-flow-col-dense' : ''}`}
              >
                <div className={!isEven && !isRTL ? 'lg:col-start-2' : ''}>
                  <span className="text-xs font-semibold uppercase tracking-widest text-gold mb-3 block">{sec.tag}</span>
                  <h2 className="text-2xl sm:text-3xl font-light tracking-tight text-foreground mb-4">{sec.h}</h2>
                  <p className="text-muted-foreground leading-relaxed mb-6">{sec.desc}</p>
                  <ul className="space-y-2.5">
                    {sec.points.map((pt, j) => (
                      <li key={j} className="flex items-start gap-3">
                        <CheckCircle2 className="h-4 w-4 text-gold mt-0.5 shrink-0" />
                        <span className="text-sm text-muted-foreground">{pt}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={`flex justify-center ${!isEven && !isRTL ? 'lg:col-start-1 lg:row-start-1' : ''}`}>
                  <div className="relative">
                    <div className="absolute -inset-8 bg-gradient-to-br from-gold/5 to-indigo-500/5 rounded-3xl blur-2xl" />
                    <div className="relative w-48 h-48 rounded-2xl border border-border bg-card flex items-center justify-center shadow-lg">
                      <Icon className="h-20 w-20 text-foreground/10" strokeWidth={1} />
                      <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-transparent to-gold/5" />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Trust Strip */}
      <section className="py-14 bg-card border-t border-border">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
            {c.trustItems.map((item, i) => (
              <div key={i} className="flex items-center gap-2.5">
                <CheckCircle2 className="h-4 w-4 text-gold shrink-0" />
                <span className="text-sm text-muted-foreground">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default FeaturesPage;
